
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { WinnersTable } from "./WinnersTable";
import { ClearWinnersDialog } from "./ClearWinnersDialog";
import type { Winner } from "./types";

interface WinnersManagementProps {
  winners: Winner[];
  onRefresh: () => void;
}

export function WinnersManagement({ winners, onRefresh }: WinnersManagementProps) {
  const { toast } = useToast();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isClearingAll, setIsClearingAll] = useState(false);

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      console.log('Attempting to delete winner with ID:', id);

      const { error } = await supabase
        .from("winners")
        .delete()
        .eq("id", id);

      if (error) {
        console.error('Error deleting winner:', error);
        throw error;
      }

      console.log('Delete operation successful');
      toast({ title: "Success", description: "Winner entry deleted successfully" });
      onRefresh();
    } catch (error) {
      console.error("Error in handleDelete:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete winner entry. Please check if you have admin permissions.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearAllWinners = async () => {
    try {
      setIsClearingAll(true);
      console.log('Attempting to clear all winners');

      const { error } = await supabase
        .from("winners")
        .delete()
        .neq("id", 0); // Delete all records

      if (error) {
        console.error('Error clearing winners:', error);
        throw error;
      }

      console.log('Clear all winners operation successful');
      toast({ 
        title: "Success", 
        description: "All winner entries have been cleared" 
      });
      onRefresh();
    } catch (error) {
      console.error("Error in clearAllWinners:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to clear winners. Please check if you have admin permissions.",
      });
    } finally {
      setIsClearingAll(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Prize Allocation Log</h2>
        <ClearWinnersDialog 
          winners={winners}
          onClear={handleClearAllWinners}
          isClearingAll={isClearingAll}
        />
      </div>

      <div className="overflow-x-auto">
        <WinnersTable
          winners={winners}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
      </div>
    </Card>
  );
}
